import React, { useState, useMemo, useEffect } from "react";
import { LatexRenderer } from "../../../components/latex-renderer";
import "./styles.sass";

const CHOICE_KEY_MAP = {
  c0: "Option A",
  c1: "Option B",
  c2: "Option C",
  c3: "Option D",
  c4: "Option E",
};

const isSkipped = (answer) =>
  !answer || String(answer).trim().toLowerCase() === "not_answered";

const formatChosenAnswer = (answer) => {
  if (isSkipped(answer)) return "Not Answered (Skipped)";
  const key = String(answer).trim().toLowerCase();
  return CHOICE_KEY_MAP[key] || answer;
};

const formatTime = (ms) => {
  if (ms === undefined || ms === null || isNaN(ms) || ms === 0) return "0s";
  if (ms < 1000) return `${ms}ms`;
  const totalSeconds = Math.round(ms / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}m ${seconds}s`;
};

const formatTimestamp = (dateStr) => {
  if (!dateStr) return "-";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return dateStr;
  }
};

export const TestQuestionDetailsModal = ({
  open,
  onClose,
  onBack,
  data,
  loading = false,
  selectedTest = null,
}) => {
  const [filterTab, setFilterTab] = useState("all");

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && open) {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  // Reset tab when modal opens
  useEffect(() => {
    if (open) {
      setFilterTab("all");
    }
  }, [open]);

  const questions = useMemo(() => {
    if (!data) return [];
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data?.questions)) return data.questions;
    if (Array.isArray(data)) return data;
    return [];
  }, [data]);

  const stats = useMemo(() => {
    const total = data?.total_questions_attempted ?? questions.length;
    let correct = 0;
    let skipped = 0;
    let incorrect = 0;

    questions.forEach((q) => {
      if (q.is_correct === true || q.is_correct === "true" || q.is_correct === 1) {
        correct += 1;
      } else if (isSkipped(q.chosen_answer)) {
        skipped += 1;
      } else {
        incorrect += 1;
      }
    });

    const answered = total - skipped;
    const accuracy = answered > 0 ? Math.round((correct / answered) * 100) : 0;

    return { total, correct, incorrect, skipped, accuracy };
  }, [data, questions]);

  const filteredQuestions = useMemo(() => {
    if (filterTab === "correct") {
      return questions.filter(
        (q) => q.is_correct === true || q.is_correct === "true" || q.is_correct === 1
      );
    }
    if (filterTab === "incorrect") {
      return questions.filter(
        (q) =>
          !(q.is_correct === true || q.is_correct === "true" || q.is_correct === 1) &&
          !isSkipped(q.chosen_answer)
      );
    }
    if (filterTab === "skipped") {
      return questions.filter((q) => isSkipped(q.chosen_answer));
    }
    return questions;
  }, [questions, filterTab]);

  if (!open) return null;

  const testName = data?.test_name || selectedTest?.test_name || selectedTest?.chapter_name || "Test Details";
  const roomCode = data?.room_code || selectedTest?.room_code;
  const studentEmail = data?.student_email || selectedTest?.student_email || selectedTest?.email;

  return (
    <div className="test_question_modal_overlay" onClick={onClose}>
      <div
        className="test_question_modal_container"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="test_question_modal_header">
          <div className="test_question_header_left">
            <div className="modal_title_row">
              {onBack && (
                <button
                  type="button"
                  className="test_modal_back_btn"
                  onClick={onBack}
                  title="Back to AI Summary"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="m15 18-6-6 6-6" />
                  </svg>
                  <span>AI Summary</span>
                </button>
              )}
              <h2>Test Question Details</h2>
              <span className="test_badge_name">{testName}</span>
              {roomCode && (
                <span className="room_code_badge">Room: {roomCode}</span>
              )}
            </div>
            {studentEmail && (
              <p className="modal_subtitle">
                Student: <strong>{studentEmail}</strong>
              </p>
            )}
          </div>

          <button
            className="test_question_close_btn"
            onClick={onClose}
            title="Close"
          >
            ×
          </button>
        </div>

        {/* Stats Bar */}
        <div className="test_question_stats_bar">
          <div className="test_stat_item">
            <div className="stat_icon total">Q</div>
            <div className="stat_info">
              <span>Attempted</span>
              <h3>{stats.total}</h3>
            </div>
          </div>

          <div className="test_stat_item">
            <div className="stat_icon correct">✓</div>
            <div className="stat_info">
              <span>Correct</span>
              <h3 style={{ color: "#059669" }}>{stats.correct}</h3>
            </div>
          </div>

          <div className="test_stat_item">
            <div className="stat_icon incorrect">✕</div>
            <div className="stat_info">
              <span>Incorrect</span>
              <h3 style={{ color: "#DC2626" }}>{stats.incorrect}</h3>
            </div>
          </div>

          <div className="test_stat_item">
            <div className="stat_icon skipped">–</div>
            <div className="stat_info">
              <span>Skipped</span>
              <h3 style={{ color: "#D97706" }}>{stats.skipped}</h3>
            </div>
          </div>

          <div className="test_stat_item">
            <div className="stat_icon accuracy">%</div>
            <div className="stat_info">
              <span>Accuracy</span>
              <h3 style={{ color: "#2563EB" }}>{stats.accuracy}%</h3>
            </div>
          </div>
        </div>

        {/* Filter Bar */}
        {!loading && questions.length > 0 && (
          <div className="test_question_filter_bar">
            <button
              className={`filter_tab_btn ${filterTab === "all" ? "active" : ""}`}
              onClick={() => setFilterTab("all")}
            >
              All ({questions.length})
            </button>
            <button
              className={`filter_tab_btn correct_tab ${filterTab === "correct" ? "active" : ""}`}
              onClick={() => setFilterTab("correct")}
            >
              ✓ Correct ({stats.correct})
            </button>
            <button
              className={`filter_tab_btn incorrect_tab ${filterTab === "incorrect" ? "active" : ""}`}
              onClick={() => setFilterTab("incorrect")}
            >
              ✕ Incorrect ({stats.incorrect})
            </button>
            {stats.skipped > 0 && (
              <button
                className={`filter_tab_btn skipped_tab ${filterTab === "skipped" ? "active" : ""}`}
                onClick={() => setFilterTab("skipped")}
              >
                – Skipped ({stats.skipped})
              </button>
            )}
          </div>
        )}

        {/* Modal Body */}
        <div className="test_question_modal_body">
          {loading ? (
            <div className="test_question_loading">
              <div className="spinner"></div>
              <p>Fetching question details for this test...</p>
            </div>
          ) : filteredQuestions.length === 0 ? (
            <div className="test_question_empty">
              <p>
                {questions.length === 0
                  ? "No question responses recorded for this test."
                  : `No ${filterTab} questions found.`}
              </p>
            </div>
          ) : (
            filteredQuestions.map((q, idx) => {
              const isCorrect =
                q.is_correct === true ||
                q.is_correct === "true" ||
                q.is_correct === 1;
              const skipped = isSkipped(q.chosen_answer);

              const cardClass = isCorrect
                ? "is_correct_card"
                : skipped
                ? "is_skipped_card"
                : "is_incorrect_card";

              return (
                <div key={idx} className={`question_card ${cardClass}`}>
                  <div className="question_card_top">
                    <div className="question_top_left">
                      <span className="q_num_badge">Question #{idx + 1}</span>
                      {q.attempt_number !== undefined && (
                        <span className="attempt_badge">
                          Attempt #{q.attempt_number}
                        </span>
                      )}
                    </div>

                    <div className="question_top_right">
                      {q.time_taken_ms !== undefined && q.time_taken_ms !== null && q.time_taken_ms > 0 && (
                        <span className="time_chip" title="Time taken">
                          <svg
                            className="time_icon"
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <circle cx="12" cy="12" r="10" />
                            <polyline points="12 6 12 12 16 14" />
                          </svg>
                          <span className="time_text">{formatTime(q.time_taken_ms)}</span>
                        </span>
                      )}
                      <span
                        className={`status_badge ${
                          isCorrect ? "correct" : skipped ? "skipped" : "incorrect"
                        }`}
                      >
                        {isCorrect ? "✓ Correct" : skipped ? "– Skipped" : "✕ Incorrect"}
                      </span>
                    </div>
                  </div>

                  {/* Question Content */}
                  <div className="question_content_box">
                    <LatexRenderer text={q.question_text || ""} />
                  </div>

                  {/* Chosen Answer — hidden for now, will be added later */}
                  {/* {q.chosen_answer !== undefined && q.chosen_answer !== null && (
                    <div
                      className={`chosen_answer_box ${
                        isCorrect ? "correct" : skipped ? "skipped" : "incorrect"
                      }`}
                    >
                      <span className="answer_label">
                        {skipped ? "Status:" : "Student's Chosen Answer:"}
                      </span>
                      <span className="answer_value">
                        <LatexRenderer
                          text={formatChosenAnswer(q.chosen_answer)}
                          inline
                        />
                      </span>
                    </div>
                  )} */}

                  {/* Footer */}
                  {q.answered_at && (
                    <div className="question_card_footer">
                      <span>Answered: {formatTimestamp(q.answered_at)}</span>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="test_question_modal_footer">
          {onBack ? (
            <button
              type="button"
              className="test_footer_back_btn"
              onClick={onBack}
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="m15 18-6-6 6-6" />
              </svg>
              <span>Back to AI Summary</span>
            </button>
          ) : (
            <div />
          )}
          <button className="done_btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default TestQuestionDetailsModal;
